import { createTranslator, type Translate } from '@platform/i18n';
import { createContext, useContext, type ReactNode } from 'react';
import type { KeyNames } from './actions/shortcut-label';
import { editorLabelsFor, type RichTextEditorLabels } from './editor/labels';

/**
 * Generic labels used inside components and composites (DESIGN.md §12: composites have an
 * i18n-free API). They come from the language packs like every other text: `PlatformProvider`
 * maps them for the current language; outside it (tests, stories) the English pack is used.
 */
export interface UiStrings {
  close: string;
  cancel: string;
  search: string;
  loading: string;
  retry: string;
  loadMore: string;
  noResults: string;
  selectAll: string;
  selectRow: string;
  sortAscending: string;
  sortDescending: string;
  /** Contains `{count}`. */
  selectedCount: string;
  openNavigation: string;
  collapseNavigation: string;
  expandNavigation: string;
  mainNavigation: string;
  userMenu: string;
  skipToContent: string;
  commandPlaceholder: string;
  /** Key caps of the command palette shortcut, e.g. "Ctrl K" / "Strg K". */
  commandShortcut: string;
  pages: string;
  previous: string;
  next: string;
  wider: string;
  openAsPage: string;
  closeDetails: string;
  breadcrumb: string;
  errorTitle: string;
  errorDescription: string;
  /** Accessible name of action menus (context menu, action sheet). */
  actions: string;
  /** "…" overflow button. */
  moreActions: string;
  /** Badge on placeholder actions of planned features. */
  planned: string;
  /** Key caps in menus and tooltips. */
  keys: KeyNames;
  /**
   * Pack lookup for registry-driven surfaces (action labels are pack keys, DESIGN.md §18). Same
   * translator as the other strings, so it works with and without `PlatformProvider`.
   */
  translate: Translate;
  /** Rich-text editor (toolbar, link dialog, plain-text switch). */
  editor: RichTextEditorLabels;
}

/** The one mapping from pack keys to component labels. */
export function uiStringsFor(t: Translate): UiStrings {
  return {
    close: t('common.close'),
    cancel: t('common.cancel'),
    search: t('common.search'),
    loading: t('common.loading'),
    retry: t('common.retry'),
    loadMore: t('common.loadMore'),
    noResults: t('table.noResults'),
    selectAll: t('table.selectAll'),
    selectRow: t('table.selectRow'),
    sortAscending: t('table.sortAscending'),
    sortDescending: t('table.sortDescending'),
    selectedCount: t('table.selectedCount'),
    openNavigation: t('shell.openNavigation'),
    collapseNavigation: t('shell.collapseNavigation'),
    expandNavigation: t('shell.expandNavigation'),
    mainNavigation: t('shell.mainNavigation'),
    userMenu: t('shell.userMenu'),
    skipToContent: t('shell.skipToContent'),
    commandPlaceholder: t('shell.commandPlaceholder'),
    commandShortcut: t('shell.commandShortcut'),
    pages: t('shell.mainNavigation'),
    previous: t('shell.previous'),
    next: t('shell.next'),
    wider: t('shell.wider'),
    openAsPage: t('shell.openAsPage'),
    closeDetails: t('shell.closeDetails'),
    breadcrumb: t('shell.breadcrumb'),
    errorTitle: t('common.errorTitle'),
    errorDescription: t('common.errorDescription'),
    actions: t('common.actions'),
    moreActions: t('common.moreActions'),
    planned: t('common.planned'),
    keys: {
      mod: t('shell.keyCtrl'),
      shift: t('shell.keyShift'),
      alt: t('shell.keyAlt'),
      delete: t('shell.keyDelete'),
      insert: t('shell.keyInsert'),
      enter: t('shell.keyEnter'),
    },
    translate: t,
    editor: editorLabelsFor(t),
  };
}

/** English labels from the reference pack (no provider needed). */
export const DEFAULT_UI_STRINGS: UiStrings = uiStringsFor(createTranslator('en'));

const UiStringsContext = createContext<UiStrings>(DEFAULT_UI_STRINGS);

export function UiStringsProvider({ strings, children }: { strings: UiStrings; children: ReactNode }) {
  return <UiStringsContext.Provider value={strings}>{children}</UiStringsContext.Provider>;
}

export function useUiStrings(): UiStrings {
  return useContext(UiStringsContext);
}
